import * as React from 'react';
import PropTypes from 'prop-types';

import CarouselControl from './CarouselControl';
import CarouselProgress from './CarouselProgress';
import CarouselSlides from './CarouselSlides';
import CarouselSlide from './CarouselSlide';
import CarouselTabs from './CarouselTabs';
import { CarouselProvider } from './CarouselContext';

/**
 * A carousel component.
 *
 * Example usage:
 *
 * ```jsx
 * <Carousel>
 *   <Carousel.Slides>
 *      {images.map(({ id, height, key, src, width }) => (
 *          <Carousel.Slide key={id}>
 *            <img key={key} src={src} height={height} width={width} />
 *          </Carousel.Slide>
 *      ))}
 *    />
 *   </Carousel.Slides>
 * <Carousel>
 * ```
 *
 * A number of additional components are provided as properties of
 * `<Carousel>`, including:
 *
 * - `<Carousel.Control>`
 * - `<Carousel.Progress>`
 * - `<Carousel.Slides>`
 * - `<Carousel.Tabs>`
 *
 * Note that these components may *only* be used as children of `<Carouel>`.
 *
 * Carousel options may be passed as component props.
 *
 * ```jsx
 * <Carousel alignSlides="center" autoplay={{ interval: 5000 }}>
 *   // ...
 * ```
 *
 * Alternatively, the `useCarousel()` hook may be used to directly reference
 * the carousel's state. When using the hook, be sure to pass it's entire
 * return value to the component via the `carousel` prop (other props will be
 * ignored in such a case).
 *
 * ```jsx
 * const carousel = useCarousel({
 *   alignSLides: "center",
 *   autoplay: { interval: 5000 }
 * });
 *
 * console.log(
 *   `The current slide is slide number ${carousel.state.current}`
 * );
 *
 * <Carousel carousel={carousel}>
 *   // ...
 * ```
 */
export default function Carousel({ children, ...props }) {
  return <CarouselProvider {...props}>{children}</CarouselProvider>;
}

Carousel.Control = CarouselControl;
Carousel.Progress = CarouselProgress;
Carousel.Slides = CarouselSlides;
Carousel.Slide = CarouselSlide;
Carousel.Tabs = CarouselTabs;

const CarouselOptions = {
  alignSlides: PropTypes.oneOf(['center', 'end', 'start', 'stretch']),
  autoplay: PropTypes.oneOf([
    PropTypes.boolean,
    PropTypes.shape({
      interval: PropTypes.number,
      pauseOnHover: PropTypes.bool,
    }),
  ]),
  clickToSlide: PropTypes.bool,
  gap: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  initialSlide: PropTypes.number,
  justifySlides: PropTypes.oneOf(['center', 'start']),
  offset: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  mode: PropTypes.oneOf(['fade', 'slide']),
};

const CarouselBundle = {
  autoplayProgress: PropTypes.object.isRequired,
  getSlidesContainerProps: PropTypes.func.isRequired,
  next: PropTypes.func.isRequired,
  pause: PropTypes.func.isRequired,
  play: PropTypes.func.isRequired,
  previous: PropTypes.func.isRequired,
  state: PropTypes.object.isRequired,
  turn: PropTypes.func.isRequired,
};

Carousel.propTypes = {
  carousel: PropTypes.shape(CarouselBundle),
  ...CarouselOptions,
};
